/** Cancel a PENDING deletion request (TMMPDM-0013). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isValidationFailed, validationFields } from '@/api/errors';

interface CancelDeletionDialogProps {
  open: boolean;
  slug: string;
  requestId: number;
  /** Whose deletion request it is — the object the title names. */
  person: string;
  onClose: () => void;
  onDone: () => void;
}

export function CancelDeletionDialog({
  open,
  slug,
  requestId,
  person,
  onClose,
  onDone,
}: CancelDeletionDialogProps) {
  // Held here, so Esc and a click outside wait for a request that is running (as ConfirmDialog does).
  const [submitting, setSubmitting] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      {open ? (
        <CancelDeletionForm
          slug={slug}
          requestId={requestId}
          person={person}
          submitting={submitting}
          setSubmitting={setSubmitting}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function CancelDeletionForm({
  slug,
  requestId,
  person,
  submitting,
  setSubmitting,
  onClose,
  onDone,
}: Omit<CancelDeletionDialogProps, 'open'> & {
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
}) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.cancelDeletion(slug, requestId, reason.trim() || undefined);
      onDone();
    } catch (err: unknown) {
      if (isValidationFailed(err)) {
        const fields = validationFields(err, t);
        setFieldError(fields.reason ?? errorCopy(err, t, ''));
      } else {
        setError(
          errorCopy(
            err,
            t,
            t('deletions.cancelDialog.error', "Couldn't cancel the deletion request."),
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('deletions.cancelDialog.titleNamed', 'Cancel the deletion request for {{person}}?', {
          person,
        })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            {t('deletions.cancelDialog.consequence', 'Nothing this person owns will be erased.')}
          </DialogContentText>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('deletions.cancelDialog.reason', 'Reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError ?? t('deletions.counter', '{{n}}/200', { n: reason.length })}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('deletions.cancelDialog.keep', 'Keep deletion request')}
        </Button>
        <SubmitButton color="error" onClick={() => void submit()} busy={submitting}>
          {t('deletions.cancelDialog.confirm', 'Cancel deletion')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
